import {SafeRecord} from '../../types/SafeRecord';
import {DANGEROUS_KEYS} from './DANGEROUS_KEYS';

type PathSegment = string | number;

function assertValidSegment(segment: unknown) {
  const isString = typeof segment === 'string';
  const isNumber = typeof segment === 'number';

  if (!isString && !isNumber) {
    throw new Error(`Key cannot be accessed.`);
  }

  if (isString && DANGEROUS_KEYS.includes(segment as (typeof DANGEROUS_KEYS)[number])) {
    throw new Error(`Key cannot be accessed.`);
  }
}

export function getByPathSafely<T extends SafeRecord>(
  obj: T | null | undefined,
  path: string | ReadonlyArray<PathSegment>,
  defaultValue?: unknown
) {
  if (typeof path !== 'string' && !Array.isArray(path)) {
    throw new Error(`Key cannot be accessed.`);
  }

  let segments: PathSegment[];
  if (Array.isArray(path)) {
    segments = [...path];
  } else if (path === '') {
    segments = [];
  } else {
    segments = (path as string).split('.');
  }

  if (segments.length === 0) {
    return obj as unknown;
  }

  let current: unknown = obj as unknown;

  // Validate all segments upfront to prevent traversing paths containing dangerous keys
  segments.forEach(assertValidSegment);

  for (const segment of segments) {
    if (!current || typeof current !== 'object') {
      return defaultValue;
    }

    const container = current as Record<string | number, unknown>;

    if (Object.prototype.hasOwnProperty.call(container, segment)) {
      // Safe dynamic property access - segment validated above for dangerous keys
      // eslint-disable-next-line eag/safe-object-access
      current = container[segment];
      continue;
    }

    return defaultValue;
  }

  return current as unknown;
}
